export const IMAGES = {
  hero: "/photos/home-hero.jpg",
  anniversaryBall: "/2027-save-the-date.png",
  engine11: "https://customer-assets.emergentagent.com/job_phoenix-detroit-fire/artifacts/1zoroaf7_Eng%2011.jpg",
  // Per-page hero / section keys (split from the old shared `helmet` placeholder
  // so each spot can be replaced with its own real Phoenix photo independently).
  aboutHero: "/photos/about-hero.jpg",
  donateHero: "/photos/donate-hero.jpg",
  membershipHero: "/photos/membership-hero.jpg",
  membershipRetireeBg: "/photos/membership-retiree-bg.jpg?v=2",
  homeStatBg: "/photos/home-stat-bg.jpg",
  detroit: "/photos/contact-detroit.jpg",
  // Project 100 callout image (rendered on /programs-outreach next to the
  // "Building the next 100 firefighters" callout). Refreshed May 22, 2026
  // with a recent academy/graduation photo showing three Black DFD members
  // in Class B duty uniform with silver badges. Replaces the prior image
  // that featured an individual the Phoenix did not want spotlighted.
  project100: "/photos/project-100-callout.jpg?v=4",
  outreach: "/photos/programs-hero.jpg",
  // Events & News hero + featured event card (added May 22, 2026).
  // Replaces the prior IMAGES.detroit usage on /events-news only.
  // Privacy + Terms continue to use IMAGES.detroit unchanged.
  eventsHero: "/photos/events-hero.jpg",
};

// ---------- Phoenix in Motion — Events & News gallery (added May 23, 2026) ----------
// Restrained 10-photo gallery surfaced on /events-news only. Phoenix members,
// legacy, fellowship, Project 100, community work, and public-facing activity.
// Ordering is intentional: legacy/founders → Project 100 → community → present-day
// fellowship → brotherhood / sisterhood. Distinct from the Historic Engine 11
// venue gallery on /historic-engine-11 (do not cross-reference).
export const PHOENIX_IN_MOTION = [
  { src: "/photos/phoenix-in-motion/pim-01-first-iabpff-convention.jpg", caption: "Phoenix at First IABPFF Convention", alt: "Phoenix delegation at the first IABPFF Convention." },
  { src: "/photos/phoenix-in-motion/pim-02-founding-fathers.jpg", caption: "Phoenix Founding Fathers", alt: "Phoenix founding fathers gathered together for a formal portrait." },
  { src: "/photos/phoenix-in-motion/pim-03-first-project100-cohort.jpg", caption: "First Project 100 Cohort Includes Zoser Ahmad", alt: "The first Project 100 cohort, including Zoser Ahmad." },
  { src: "/photos/phoenix-in-motion/pim-04-project100-graduate.jpg", caption: "Project 100 Candidate Graduates DFD Academy", alt: "A Project 100 candidate at her Detroit Fire Department academy graduation, holding her certificate with family." },
  { src: "/photos/phoenix-in-motion/pim-05-community-outreach-fun.jpg", caption: "Community Outreach Fun", alt: "Phoenix community outreach — joyful moment with young Detroit attendees at an outdoor event." },
  { src: "/photos/phoenix-in-motion/pim-06-backpack-giveaway.jpg", caption: "A Backpack Giveaway Event", alt: "Phoenix backpack giveaway under event tents — rows of color-coded backpacks for Detroit students." },
  { src: "/photos/phoenix-in-motion/pim-07-phoenix-fellowship.jpg", caption: "Phoenix Fellowship", alt: "Phoenix members in fellowship at Historic Engine 11." },
  { src: "/photos/phoenix-in-motion/pim-08-phoenix-brotherhood.jpg", caption: "Phoenix Brotherhood", alt: "Phoenix brothers together at Historic Engine 11." },
  { src: "/photos/phoenix-in-motion/pim-09-firefighter-fellowship.jpg", caption: "Firefighter Fellowship", alt: "Firefighter fellowship inside a DFD gathering space." },
  { src: "/photos/phoenix-in-motion/pim-10-phoenix-sisterhood.jpg", caption: "Phoenix Sisterhood", alt: "Phoenix sisterhood — Phoenix women together at Historic Engine 11." },
  { src: "/photos/phoenix-in-motion/pim-11-united-in-service.jpg", caption: "United in Service", alt: "Phoenix members in full Class A dress uniform standing together in front of New Bethel Baptist Church." },
  { src: "/photos/phoenix-in-motion/pim-12-lady-phoenix-honors-gigi.jpg", caption: "Lady Phoenix Honors Gigi", alt: "Lady Phoenix members gathered together honoring Gigi at a Phoenix event." },
];

// ---------- Historic Engine 11 — Inside Gallery (added May 23, 2026) ----------
// Restrained 6-photo gallery surfaced on /historic-engine-11. Phoenix-approved
// venue photography only. #4 (legacy doors) includes the honored portrait of
// Capt. Marvin Massey — Phoenix is proud to display this publicly. Ordering
// is intentional: anchor banquet shot → wide hall → bar/lounge → legacy
// portraits/foyer → archway detail → upstairs boardroom (second config).
export const ENGINE11_GALLERY = [
  {
    src: "/photos/engine11/e11-banquet-reels.jpg",
    alt: "Historic Engine 11 banquet hall set for an event — antique fire hose reels displayed on the raised wood platform under a coffered wooden ceiling.",
    caption: "Banquet Hall",
  },
  {
    src: "/photos/engine11/e11-banquet-wide.jpg",
    alt: "Wide view of the Historic Engine 11 banquet hall — round tables, stage with podium, and historic Detroit fire apparatus wheels on display.",
    caption: "Event Floor",
  },
  {
    src: "/photos/engine11/e11-bar-lounge.jpg",
    alt: "The bar and lounge at Historic Engine 11 — polished wood bar, DFD-branded stools, Phoenix Detroit memorabilia, and the Fire Department doorway curtain.",
    caption: "Bar & Lounge",
  },
  {
    src: "/photos/engine11/e11-legacy-doors.jpg",
    alt: "The foyer at Historic Engine 11 — honored Phoenix legacy portraits, including Capt. Marvin Massey, flanking the ornate doors that open onto the banquet hall.",
    caption: "Legacy Foyer",
  },
  {
    src: "/photos/engine11/e11-archway.jpg",
    alt: "Ornate wooden archway inside Historic Engine 11 with DFD lettering above the doorway — the corridor leading from the foyer into the firehouse interior.",
    caption: "DFD Archway",
  },
  {
    src: "/photos/engine11/e11-boardroom.jpg",
    alt: "The upstairs meeting room at Historic Engine 11 — exposed brick walls, long dark wood conference table with leather chairs, parquet staircase with brass rails.",
    caption: "Upstairs Meeting Room",
  },
];

// ---------- Leadership (Executive Board + Board of Trustees) ----------
// Names + titles + photo URLs only — no bios per Phoenix v1 direction.
// Photos served from /app/frontend/public/leadership/*.jpg (light cleanup applied).
export const LEADERSHIP = {
  executiveBoard: [
    { name: "Teresa Singleton", title: "President", photo: "/leadership/teresa.jpg" },
    { name: "Dolphin Michael", title: "Vice President and Treasurer", photo: "/leadership/dolphin.jpg" },
    { name: "Myron Mayberry", title: "Secretary", photo: "/leadership/myron.jpg" },
  ],
  boardOfTrustees: [
    { name: "Zoser Ahmad", title: "Board of Trustees", photo: "/leadership/zoser.jpg" },
    { name: "Arnold Freeman", title: "Board of Trustees", photo: "/leadership/freeman.jpg" },
    { name: "Raymond McCants", title: "Board of Trustees", photo: "/leadership/mccants.jpg" },
    { name: "Anthony McCloud", title: "Board of Trustees", photo: "/leadership/mccloud.jpg" },
  ],
};

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/historic-engine-11", label: "Historic Engine 11" },
  { to: "/programs-outreach", label: "Programs & Outreach" },
  { to: "/2027", label: "2027 Conference & 60th Anniversary" },
  { to: "/membership", label: "Membership" },
  { to: "/rent-engine-11", label: "Rent Engine 11" },
  { to: "/events-news", label: "Events & News" },
  { to: "/contact", label: "Contact" },
];

export const ORG = {
  name: "Phoenix of the Detroit Fire Department",
  short: "The Phoenix",
  founded: 1967,
  anniversary: 2027,
  conferenceDates: "May 12–16, 2027",
  ballName: "Phoenix 60th Anniversary Gala: The Detroit Renaissance Ball",
  email: "8phoenixrising@gmail.com",
  emailConfirmed: true,
  // Dedicated conference email (created May 22, 2026) — NOT yet wired into any
  // visible UI or any /api/* forwarding flow. Recorded here for deployment
  // planning. To be used (once email forwarding is authorized) for ALL
  // communications related to: the 2027 North Central Region Spring
  // Conference, the Phoenix 60th Anniversary Celebration, the Harlem
  // Renaissance Ball, conference volunteer, conference sponsorship, ad
  // journal placements, table sponsorships, and the October 2026 NCR
  // Presidents Meeting. General Phoenix business (membership, rentals,
  // outreach, general donations, day-to-day operations) continues to route
  // to `email` above.
  conferenceEmail: "DetroitPhoenix2027@gmail.com",
  conferenceEmailConfirmed: true,
  phone: "313-597-3590",
  address: "Historic Engine 11, 2733 Gratiot Ave., Detroit, MI 48207",
  mapsQuery: "2733 Gratiot Ave, Detroit, MI 48207",
  website: "www.detroitfirefighter.com",
  websiteUrl: "https://www.detroitfirefighter.com",
};

// Official division list — use this exact wording wherever the full list
// is named. For shorter areas, prefer the broader inclusive phrasing
// "active Detroit Fire Department members across all divisions" or
// "active and retired Detroit Fire Department members across all divisions".
export const DFD_DIVISIONS = [
  "Fire Operations",
  "Emergency Medical Services",
  "Fire Marshal",
  "Fire Investigation",
  "Training",
  "Communications",
  "Community Relations",
  "Apparatus",
];
// Comma-list helper for inline copy (kept here so wording stays consistent sitewide).
export const DFD_DIVISIONS_INLINE = "Fire Operations, Emergency Medical Services, Fire Marshal, Fire Investigation, Training, Communications, Community Relations, and Apparatus";

// ---------- Brand / Logo ----------
// Official Phoenix of the Detroit Fire Department logo.
export const BRAND = {
  logoUrl: "https://customer-assets.emergentagent.com/job_phoenix-detroit-fire/artifacts/ltu85bvy_Phoenix%2067.png",
  logoAlt: "Phoenix of the Detroit Fire Department — official logo",
};

// ---------- Affiliation logos (used on /2027 in a restrained protocol band) ----------
export const AFFILIATIONS = {
  iabpff: {
    name: "IABPFF International",
    motto: "Keep the Fire Burning for Justice",
    imageUrl: "/logos/iabpff-international.png",
    alt: "International Association of Black Professional Fire Fighters — IABPFF International badge",
  },
  ncr: {
    name: "North Central Region",
    motto: "All That We Are We Owe",
    imageUrl: "/logos/ncr-iabpff.png",
    alt: "IABPFF North Central Region badge",
  },
};

// ---------- Additional Ways to Give (supplementary to Zeffy; rendered on /donate-sponsor) ----------
// Zeffy remains the official platform for conference registration, gala tickets,
// sponsorships, tables, and ad journal forms. These options support direct,
// general donations only.
export const PAYMENTS = {
  cashapp: {
    handle: "$DetroitPhoenix",
    payUrl: "https://cash.app/$DetroitPhoenix",
  },
  paypal: {
    label: "Phoenix PayPal",
    // Cleaner PayPal Checkout link issued by Phoenix on May 23, 2026.
    // Replaces the prior PayPal.Me/phoenixtshirt link (which referenced an
    // older T-shirt setup). This is a PayPal NCP checkout URL — guest-
    // friendly (no PayPal login required for the sender).
    payUrl: "https://www.paypal.com/ncp/payment/HCCNGLSC9DMNY",
    payUrlReady: true,
    // PayPal QR re-issued May 23, 2026 (labeled "DetroitPhoenix"). The QR
    // resolves to the same NCP checkout flow above.
    qrUrl: "/payments/paypal-qr.png",
    qrReady: true,
  },
  zelle: {
    label: "Phoenix Zelle",
    email: "8phoenixrising@gmail.com",
    // Zelle QR re-enabled May 23, 2026 after explicit decode verification:
    // the QR decodes to the official `enroll.zellepay.com/qr-codes` URL with
    // the correct payee name and token. Crop is generous to preserve all
    // alignment squares.
    qrUrl: "/payments/zelle-qr.png",
    qrReady: true,
  },
};

// ---------- Social Media ----------
export const SOCIAL = {
  facebookUrl: "https://www.facebook.com/PhoenixDFD",
  facebookLabel: "Phoenix of the Detroit Fire Department",
  instagram: "https://www.instagram.com/thephoenixdetroit",
  instagramHandle: "@thephoenixdetroit",
};

// ---------- Host Hotel / Event Location (2027 NCR Conference + 60th Gala) ----------
export const HOST_HOTEL = {
  name: "Hollywood Casino Greektown",
  city: "Detroit, Michigan",
  address: "1200 St. Antoine, Detroit, MI 48226",
  mapsQuery: "1200 St. Antoine, Detroit, MI 48226",
  reservationPhone: "877-424-5554",
  bookingLink: "https://book.rguest.com/onecart/wbe/calendar/688/hollywoodgreektown/DETP27E",
  groupCode: "DETP27E",
  cutoffDate: "April 12, 2027",
  confirmed: true,
};

// ---------- Monthly Retirees Breakfast (Recurring · 1st Wed, Apr–Sep) ----------
// Flyer is fully evergreen ("1st Wednesday of Every Month") — only `nextDate`
// changes month-to-month and is shown in the structured website details.
export const RETIREES_BREAKFAST = {
  title: "Monthly Retirees Breakfast",
  theme: "Eastside / Westside Retirees · Bridging the Gap",
  schedule: "Held the first Wednesday of each month. The next date will be announced separately through the Phoenix member text blast.",
  nextDate: "Announced via Phoenix member text blast",
  doorsOpen: "9:00 a.m.",
  location: "Historic Engine 11, 2733 Gratiot Ave., Detroit, MI 48207",
  cost: "$15",
  caterer: "Last Minute Gourmet",
  flyerImage: "/events/retirees-breakfast-flyer.jpg?v=5",
};

// ---------- Annual Phoenix Retirees Luncheon (FLAGGED · not yet wired) ----------
// Evergreen flyer is uploaded. Date is announced separately via member text
// blast. When ready to publish on the Events & News page, surface this with
// `RETIREES_LUNCHEON.nextDate` populated in the structured event details.
// ⚠️ FLYER REVIEW — "RSVP by text" line may need to be removed (item 24, May 2026)
// The Annual Phoenix Retirees Luncheon flyer image (`flyerImage`) still has
// "RSVP by text" + phone number printed on the artwork itself. RSVP/contact
// handling may move to the website inquiry form post-launch — at which point
// the flyer image will need a small reissue to remove that line. The
// structured `rsvpInstruction` field below is rendered in HTML and CAN be
// changed here at any time independently of the printed flyer art.
export const RETIREES_LUNCHEON = {
  title: "Annual Phoenix Retirees Luncheon",
  tagline: "Sound the Alarm — Don't Miss This Run!",
  location: "Historic Engine 11, 2733 Gratiot Ave., Detroit, MI 48207",
  highlights: ["Good Food", "Music", "BYOB", "See old friends"],
  rsvpPhone: "313-597-3590",
  rsvpInstruction: "RSVP by text",
  host: "Phoenix of the Detroit Fire Department",
  nextDate: "", // populated when announced
  flyerImage: "/events/retirees-luncheon-flyer.jpg?v=2",
};

// ---------- Sivad Johnson Scholarship Program (FLAGGED · not yet wired) ----------
// Evergreen flyer is uploaded — essay topic and application deadline are
// announced each year when the scholarship application period opens.
// Presented in honor of Sivad Johnson.
export const SIVAD_JOHNSON_SCHOLARSHIP = {
  title: "Sivad Johnson Scholarship Program",
  tagline: "Honoring Sacrifice, Igniting Futures",
  honoree: "Presented in honor of Sivad Johnson.",
  essayTopic: "The essay topic will be announced when the scholarship application period opens.",
  deadline: "The application deadline will be announced with the opening of the scholarship application period.",
  essayLength: "1,000 minimum – 2,000 maximum words, typed, double-spaced",
  awards: "Two $500 scholarships awarded",
  eligibility: [
    "Sponsor must be a Phoenix member",
    "Applicant must be a qualifying dependent of a Detroit Firefighter (any rank)",
    "Must provide documentation of acceptance or enrollment to a community college or other accredited college/university",
    "Carry a minimum 2.5 GPA",
    "Complete essay as outlined above",
    "All submissions must be original work; use of AI tools is not permitted",
    "All entries will receive a confirmation email from the committee",
  ],
  contactPhone: "313-597-3590",
  contactEmail: "8phoenixrising@gmail.com",
  website: "www.detroitfirefighter.com",
  location: "Phoenix of the Detroit Fire Department · 2733 Gratiot, Detroit, MI 48207",
  flyerImage: "/events/sivad-johnson-scholarship-flyer.jpg?v=5",
};

// ---------- Phoenix Legacy Project · Faces Never Forgotten (FLAGGED · not yet wired) ----------
// Ongoing Phoenix initiative — collects member photos for a dedicated wall at
// Historic Engine 11. Not limited to Black History Month; treated as an
// evergreen Phoenix legacy initiative.
export const PHOENIX_LEGACY_PROJECT = {
  title: "Faces Never Forgotten",
  subtitle: "Phoenix Legacy Project",
  summary:
    "An ongoing Phoenix initiative collecting member photos for a dedicated wall at Historic Engine 11 — preserving the history of Phoenix members and the Detroit Fire Department for future generations.",
  photoGuidance:
    "Fire department photos are encouraged — action shots in turnout gear, dress uniform, or station wear. Black-and-white or full color. Photos should be high quality and reproduce well at 8x10, 4x6, or 3x5.",
  eligibility: "Open to current Phoenix members.",
  submissionEmail: "8phoenixrising@gmail.com",
  flyerImage: "/events/phoenix-legacy-project-flyer.jpg?v=2",
};

// ---------- NCR Presidents Meeting · October 8–10, 2026 ----------
export const HOTEL_2026_PRESIDENTS = {
  eventName: "2026 North Central Region Presidents Meeting",
  dates: "October 8–10, 2026",
  checkIn: "Thursday, October 8, 2026",
  checkOut: "Saturday, October 10, 2026",
  hotel: "Hollywood Casino Greektown",
  city: "Detroit, Michigan",
  bookingLink: "https://book.rguest.com/onecart/wbe/offers/688/hollywoodgreektown?offerCode=DETB26K",
  groupCode: "DETB26K",
  reservationPhone: "877-424-5554",
  cutoffDate: "September 8, 2026",
  status: "live",
};

// ---------- Image Placeholder Tracking ----------
// Mark each entry in IMAGES that is currently a placeholder / stock image.
// When real Phoenix-supplied photography replaces the URL in IMAGES, set false.
export const IMAGE_PLACEHOLDER = {
  hero: false,            // Real Phoenix photo — Apr 2025 fellowship moment featuring President Teresa Singleton (Wave 3)
  anniversaryBall: false, // 2027 NCR + Phoenix 60th save-the-date key art (temporary; pending final polished conference branding package)
  engine11: false,       // Real photo of Historic Engine 11 facility — now in use
  aboutHero: false,       // Real Phoenix photo — Engine 11 heritage corridor (Wave 3)
  donateHero: false,      // Real Phoenix photo — Project 100 ceremony (Wave 3)
  membershipHero: false, // Real Phoenix photo — Aug 2025 ladder-truck gathering at Engine 11 (Wave 2)
  membershipRetireeBg: false, // Real Phoenix photo — Sep 2024 retiree in 1867 DFD shirt (Wave 1)
  homeStatBg: false,     // Real Engine 11 photo — antique apparatus wheels close-up (Wave 1)
  detroit: false,        // Real Phoenix photo — Jun 2025 Engine 11 exterior with DFD signage (Wave 2)
  project100: false, // Real Phoenix photo — May 2026 family graduation moment (Wave 2)
  outreach: false,        // Real Phoenix photo — Backpack Giveaway crowd with DFD ladder truck (Wave 3)
};

// Inquiry types — used by /contact form dropdown + URL ?type= prefill
export const INQUIRY_TYPES = [
  { id: "general", label: "General Inquiry" },
  { id: "60th-interest", label: "60th Anniversary Interest List" },
  { id: "sponsor-60th", label: "Sponsorship Inquiry — Phoenix 60th Anniversary" },
  { id: "sponsor-ncr", label: "Sponsorship Inquiry — 2027 NCR Conference" },
  { id: "sponsor-combined", label: "Sponsorship Inquiry — Combined / Both" },
  { id: "ad-journal", label: "Ad Journal Placement Interest" },
  { id: "table-sponsorship", label: "Table Sponsorship Interest — Phoenix 60th Anniversary Gala: The Detroit Renaissance Ball" },
  { id: "per-event", label: "Per-Event Sponsorship Interest" },
  { id: "volunteer", label: "Volunteer Interest" },
  { id: "donation", label: "Donations / Wire Transfer / In-Kind / Planned Giving" },
  { id: "membership", label: "Membership Inquiry" },
  { id: "partnership", label: "Supporter / Ally / Community Partner Inquiry" },
  { id: "project-100", label: "Project 100 / Recruitment" },
  { id: "rent-engine-11", label: "Rent Historic Engine 11" },
  { id: "share", label: "Share the Phoenix Mission" },
  { id: "media", label: "Media / Press" },
];

export const VOLUNTEER_AREAS = [
  "Community Day",
  "Outreach",
  "Project 100",
  "Setup / Logistics",
  "Registration",
  "Ball Hospitality",
  "Historic Engine 11",
  "General Volunteer",
];

// Quick Routes shown above the Contact form.
// NOTE (May 2026): The 3 Zeffy chips (Interest List / Sponsorship Inquiry /
// Volunteer Interest) were removed from this list. Reason: a user already on
// /contact is in the broad inquiry hub — bouncing them OUT to conference-
// specific Zeffy forms via generically-labeled chips violated the routing
// rule (broad → /contact form, conference-specific → explicit Zeffy CTAs on
// /2027 and /donate-sponsor only).
export const QUICK_ROUTES = [
  { label: "Programs & Outreach", to: "/programs-outreach" },
  { label: "Membership", to: "/membership" },
  { label: "Rent Engine 11", to: "/rent-engine-11" },
];
